import { useContext } from "react";

import { BTLink } from "@btnet/core-components/BTLink";
import { BTText } from "@btnet/core-components/BTText";
import { useMfaEnrollmentStateGetV2 } from "@btnet/services-btnet/handlers/mfa-enrollment-state/mfa-enrollment-state";

import { useBTLinkSpaRouting } from "helpers/globalContext/BTLinkSpaRoutingProvider";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { externalPages } from "utilities/externalPages";
import { isInPortal } from "utilities/portal/portal";

import { optInEveryLoginAndLogout } from "commonComponents/auth/AuthStoreDiscovery.utils";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTIconLockFilled } from "commonComponents/btWrappers/BTIcon";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { BlockScreen } from "commonComponents/utilities/BlockScreen/BlockScreen";
import { hasBlockSafeMarker } from "commonComponents/utilities/MfaEnrollmentNudgeBanner/MfaEnrollmentNudgeBanner.storage";

import "./MfaEnrollmentBlock.less";

import { MFA_BLOCK_COPY } from "./MfaEnrollmentBlock.constants";

const MfaStepsList: React.FC = () => (
    <div className="mfa-block-steps-container">
        <ul className="mfa-block-steps">
            {MFA_BLOCK_COPY.steps.map((step) => (
                <li key={step}>
                    <BTText text={step} size="normal-md" />
                </li>
            ))}
        </ul>
    </div>
);

const MfaEnrollmentBlockInternal: React.FC = () => {
    const userInfo = useContext(UserInfoContext);
    const userId = userInfo?.userId ?? null;
    const btLinkRouting = useBTLinkSpaRouting();

    // Only end-user portals (builder/subs/owner) can be subject to MFA enrollment. Lead/share
    // routes resolve to a non-internal portal, where the apix endpoint would 401 and trigger the
    // global login redirect — so gate both the query and the block on the live portal signal.
    const isEndUserPortal = isInPortal({ builder: true, subs: false, owner: false });

    // Skip the GET on reload only when a within-window marker proves this user is NOT force-MFA
    // (forceMfa:false). Any blockable or unprovable state re-checks against fresh server state, so a
    // server-side resolution lifts the block on the next load rather than being suppressed for 12h.
    // userId gates the fetch too: the block's CTA opts in by userId, so a block shown before the SPA
    // UserInfo payload hydrates (userId is null in the legacy globals path) would be non-functional.
    const canSkipForBlock = hasBlockSafeMarker(userId);

    const { data: response } = useMfaEnrollmentStateGetV2({
        query: { enabled: isEndUserPortal && userId != null && !canSkipForBlock },
    });

    const enrollmentState = response?.data;
    const shouldBlock =
        isEndUserPortal && enrollmentState?.isApplicable && enrollmentState?.forceMfa;

    if (!shouldBlock) {
        return null;
    }

    return (
        <BlockScreen
            icon={<BTIconLockFilled />}
            title={MFA_BLOCK_COPY.title}
            subtitle={MFA_BLOCK_COPY.subtitle}
            action={
                <>
                    <BTButton
                        data-testid="mfaEnrollmentBlock-cta"
                        type="primary"
                        onClick={() => optInEveryLoginAndLogout(userInfo?.userId ?? null)}
                    >
                        {MFA_BLOCK_COPY.primaryCta}
                    </BTButton>
                    <BTLink
                        href={externalPages.XeroMFAServiceCloudHelpDoc}
                        shouldOpenNewTab
                        routing={btLinkRouting}
                        data-testid="mfaEnrollmentBlock-learn-more"
                    >
                        {MFA_BLOCK_COPY.secondaryCta}
                    </BTLink>
                </>
            }
        >
            <div className="block-screen-title">
                <BTText text={"How it works"} size="distinct-lg" />
            </div>
            <MfaStepsList />
        </BlockScreen>
    );
};

export const MfaEnrollmentBlock = withErrorBoundary(MfaEnrollmentBlockInternal)(
    "Could not load MFA Enrollment Block",
);
